import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";
import { LOGIN, REFRESH } from "src/Api/endpoints";

const TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || "auth_token";

// Proactively refresh when the token has less than this many seconds left
// (1 day). Combined with the backend's 30-day max session, any active
// user stays logged in indefinitely; after >7d inactive, the next
// request 401s and the user is sent back to /login.
const REFRESH_THRESHOLD_SECONDS = 24 * 60 * 60;

interface TokenPayload {
  sub?: string;
  username?: string;
  exp?: number;
  iat?: number;
  origIat?: number;
}

function decodeToken(token: string | null): TokenPayload | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload as TokenPayload;
  } catch {
    return null;
  }
}

function secondsUntilExpiry(token: string | null): number | null {
  const payload = decodeToken(token);
  if (!payload?.exp) return null;
  return payload.exp - Math.floor(Date.now() / 1000);
}

// Module-scoped current-token mirror — lets non-React code (the axios
// interceptor) read the token synchronously.
let currentToken: string | null =
  typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

export const getCurrentAuthToken = () => currentToken;

// Events emitted so the React AuthProvider can stay in sync when the
// token is refreshed or invalidated by the axios interceptor.
const EVT_CHANGED = "auth:token-changed";
function emitChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVT_CHANGED));
  }
}

function storeToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
  currentToken = token;
  emitChanged();
}

// In-flight refresh guard so concurrent 401s don't trigger parallel calls.
let inFlightRefresh: Promise<string | null> | null = null;

interface RefreshOptions {
  /**
   * Whether a failed refresh should clear the stored token (logging the
   * user out). Defaults to `true`, which is correct for the authoritative
   * callers — the axios 401 interceptor and the on-mount proactive
   * refresh — where a failed refresh against a real API call means the
   * session is genuinely dead.
   *
   * Set to `false` for SECONDARY channels (e.g. a realtime WebSocket
   * reconnect) that must never be able to destroy the primary session: a
   * WS failing to connect — for transport reasons or otherwise — is not
   * proof the session is invalid. Those callers recover on success and
   * stay silent on failure; the real logout decision is left to the next
   * API 401.
   */
  clearOnFailure?: boolean;
}

/**
 * Call POST /auth/refresh using the current token. Returns the new
 * token on success, null on failure. Safe to call from anywhere
 * (React or not) and from multiple places concurrently.
 */
export async function refreshAuthToken(
  options: RefreshOptions = {}
): Promise<string | null> {
  const { clearOnFailure = true } = options;
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const res = await predictionManagerAxiosApi.post<{ access_token: string }>(
        REFRESH,
        {}
      );
      storeToken(res.data.access_token);
      return res.data.access_token;
    } catch {
      if (clearOnFailure) storeToken(null);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

interface AuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(currentToken);

  // Sync React state with module-scoped currentToken whenever the
  // interceptor (or another tab) mutates it.
  useEffect(() => {
    const handler = () => setToken(currentToken);
    window.addEventListener(EVT_CHANGED, handler);
    window.addEventListener("storage", (e) => {
      if (e.key === TOKEN_KEY) {
        currentToken = e.newValue;
        setToken(currentToken);
      }
    });
    return () => window.removeEventListener(EVT_CHANGED, handler);
  }, []);

  // On mount: proactively refresh if token is near-expiry.
  useEffect(() => {
    const ttl = secondsUntilExpiry(currentToken);
    if (ttl !== null && ttl < REFRESH_THRESHOLD_SECONDS) {
      refreshAuthToken();
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await predictionManagerAxiosApi.post<{ access_token: string }>(LOGIN, {
      username,
      password,
    });
    storeToken(res.data.access_token);
  }, []);

  const logout = useCallback(() => {
    storeToken(null);
  }, []);

  const refresh = useCallback(() => refreshAuthToken(), []);

  const value = useMemo<AuthContextValue>(
    () => ({ token, isAuthenticated: !!token, login, logout, refresh }),
    [token, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
